import { createContext, useState, useEffect, useContext } from "react";
import { decryptUsername } from "./GlobalContext";

const LeaderboardContext = createContext();

const EVO_THRONE_MIN_TURNOVER = 5000;
const EVO_THRONE_MAX_LEADERBOARD = 10;

export const LeaderboardJDBWinnerLeague = ({ children }) => {
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loadingBoard, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [playerPosition, setPlayerPosition] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const DEBUG = true;

  useEffect(() => {
    const loggedInState = localStorage.getItem("isLoggedIn") === "true";

    setIsLoggedIn(loggedInState);

    if (DEBUG) {
      console.log("[EVO THRONE] Logged in:", loggedInState);
    }
  }, []);

  const getBonusByRank = (rank) => {
    const r = parseInt(rank, 10);

    if (r === 1) return 8000;
    if (r === 2) return 5000;
    if (r === 3) return 3000;
    if (r >= 4 && r <= 5) return 1000;
    if (r >= 6 && r <= 10) return 500;

    return 0;
  };

  const getPlayerPosition = () => {
    try {
      const storedValue = localStorage.getItem("username");

      if (!storedValue) {
        return null;
      }

      const storedUsername = decryptUsername(storedValue);

      if (!storedUsername) {
        return null;
      }

      const normalizedStoredUsername = String(storedUsername)
        .trim()
        .toLowerCase();

      if (DEBUG) {
        console.log(
          "[EVO THRONE] Looking for player:",
          normalizedStoredUsername,
        );

        console.log(
          "[EVO THRONE] Qualified leaderboard usernames:",
          leaderboardData.map((item) => item.username),
        );
      }

      const found = leaderboardData.find((entry) => {
        const apiUsername = String(entry.username || "")
          .trim()
          .toLowerCase();

        return apiUsername === normalizedStoredUsername;
      });

      return found ? parseInt(found.ranking, 10) : null;
    } catch (err) {
      console.error("[EVO THRONE] Player position error:", err);

      return null;
    }
  };

  useEffect(() => {
    if (!leaderboardData.length) {
      setPlayerPosition(null);
      return;
    }

    const position = getPlayerPosition();

    setPlayerPosition(position);
  }, [leaderboardData]);

  useEffect(() => {
    if (!isLoggedIn) {
      setLoading(false);
      return;
    }

    const fetchLeaderboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const url = `${process.env.REACT_APP_API_JSON_URL_PROMO}?cmd=getEvoTOLeaderboard`;

        if (DEBUG) {
          console.log("[EVO THRONE] Fetching:", url);
        }

        const response = await fetch(url, {
          method: "GET",
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(`HTTP error: ${response.status}`);
        }

        const rawData = await response.json();

        if (DEBUG) {
          console.log("[EVO THRONE] Raw API response:", rawData);
        }

        if (!rawData || typeof rawData !== "object" || Array.isArray(rawData)) {
          throw new Error("Invalid leaderboard API response");
        }

        const parsedData = Object.entries(rawData)
          .map(([ranking, value]) => {
            if (typeof value !== "string") {
              return null;
            }

            const parts = value.split("|");

            const maskedUsername = parts[0]?.trim() || "";
            const turnover = Number(parts[1]?.trim() || 0);
            const username = parts[2]?.trim() || "";

            const rank = parseInt(ranking, 10);

            return {
              ranking: rank,
              username,
              masked: maskedUsername,
              turnover,
              freespins: getBonusByRank(rank),
            };
          })
          .filter(Boolean)

          // Only players who reached at least 5,000 turnover
          .filter(
            (player) =>
              Number.isFinite(player.turnover) &&
              player.turnover >= EVO_THRONE_MIN_TURNOVER,
          )

          // Keep leaderboard in API ranking order
          .sort((a, b) => a.ranking - b.ranking)

          // Maximum of 10 qualified leaderboard players
          .slice(0, EVO_THRONE_MAX_LEADERBOARD);

        if (DEBUG) {
          console.log(
            `[EVO THRONE] Qualified players with ${EVO_THRONE_MIN_TURNOVER.toLocaleString()}+ turnover:`,
          );

          console.table(parsedData);
        }

        setLeaderboardData(parsedData);
      } catch (err) {
        console.error("[EVO THRONE] Leaderboard API error:", err);

        setError(err);
        setLeaderboardData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboardData();
  }, [isLoggedIn]);

  return (
    <LeaderboardContext.Provider
      value={{
        leaderboardData,
        loadingBoard,
        error,
        getBonusByRank,
        getPlayerPosition,
        playerPosition,
      }}
    >
      {children}
    </LeaderboardContext.Provider>
  );
};

export const useLeaderboard = () => {
  const context = useContext(LeaderboardContext);

  if (!context) {
    throw new Error(
      "useLeaderboard must be used inside LeaderboardJDBWinnerLeague",
    );
  }

  return context;
};
